import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * En-tête de SECTION (Devices, Gateways…) — option B.
 *
 * Le H1 porte le nom de la section, constant quel que soit l'onglet, donc jamais dupliqué par
 * l'onglet actif (l'ancien duo `wh-page-header title="Supervised"` + barre d'onglets réécrivait
 * « Supervised » deux fois). Les onglets se projettent à droite en `[slot=tabs]` (rendu segmented
 * de `wh-tabs`). Une barre fine sous l'en-tête porte le sous-titre de la sous-page + ses actions —
 * elle n'apparaît que si `subtitle` est fourni ou `showToolbar` est vrai (sous-page sans contexte
 * = juste titre + onglets).
 *
 *   <wh-section-header title="Outils" subtitle="Ce que fait la sous-page.">
 *     <lib-tools-tabs slot="tabs" />
 *     <wh-button …>Action</wh-button>   // action, projetée dans la barre fine
 *   </wh-section-header>
 *
 * Sous-titre riche (liens) : ne pas passer `subtitle`, mettre `[showToolbar]="true"` et projeter
 * un élément `[slot=subtitle]`.
 */
@Component({
  selector: 'wh-section-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <h1 class="font-mono text-2xl font-semibold tracking-tight" [class]="titleClass()">{{ title() }}</h1>
      <div class="min-w-0 shrink-0">
        <ng-content select="[slot=tabs]" />
      </div>
    </div>

    @if (subtitle() || showToolbar()) {
      <div
        class="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3 dark:border-slate-700">
        <div class="min-w-0 max-w-3xl text-sm text-gray-500 dark:text-slate-400">
          @if (subtitle()) {
            {{ subtitle() }}
          } @else {
            <ng-content select="[slot=subtitle]" />
          }
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <ng-content />
        </div>
      </div>
    }
  `,
})
export class WhSectionHeaderComponent {
  readonly title = input.required<string>();
  readonly subtitle = input<string | null>(null);
  readonly showToolbar = input(false);
  readonly accent = input<'primary' | 'warmYellow' | 'aquamarine' | 'electricPurple' | 'neutral'>('primary');

  protected readonly titleClass = computed(() => {
    switch (this.accent()) {
      case 'warmYellow':
        return 'text-warmYellow-500 dark:text-warmYellow-300';
      case 'aquamarine':
        return 'text-aquamarine-900 dark:text-aquamarine-300';
      case 'electricPurple':
        return 'text-eletricPurple-500 dark:text-eletricPurple-300';
      case 'neutral':
        return 'text-gray-900 dark:text-slate-100';
      default:
        return 'text-primary-500';
    }
  });
}
